// Datenzugriff des Mieterportals – nur über die Portal-Funktionen der Datenbank (SECURITY DEFINER,
// Mieter aus auth.uid(), Portal-Schalter geprüft). Keine direkten Tabellenzugriffe, nichts wird versendet.
import { supabase, supabaseAnonKey, supabaseUrl } from '../supabase/client';
import type { PortalDokument, PortalTermin } from './mieterE5';
import { fotoTyp, kategorieLabel, raumGueltig, type Bereich, type KategorieKey, type Meldung, type MieterKontext } from './mieter';

export async function ladeMieterKontext(): Promise<MieterKontext | null> {
  const { data, error } = await supabase.rpc('portal_mieter_kontext');
  if (error) { console.error('Mieterportal: Kontext laden fehlgeschlagen', error.message); return null; }
  return (data as MieterKontext | null) ?? null;
}

export async function ladeMeineMeldungen(): Promise<Meldung[]> {
  const { data, error } = await supabase.rpc('portal_meine_meldungen');
  if (error) { console.error('Mieterportal: Meldungen laden fehlgeschlagen', error.message); return []; }
  return (data as Meldung[] | null) ?? [];
}

export interface SchadenMeldung {
  kategorie: KategorieKey;
  bereich: Bereich;
  raum: string | null;
  beschreibung: string;
  zutritt: boolean;
  dringend: boolean;
  fotos: File[];
}

/** Legt die Meldung an und lädt danach die Fotos hoch. Fotos, die scheitern, werden gezählt – die Meldung bleibt. */
export async function schadenMelden(m: SchadenMeldung): Promise<{ ticketId: string; ticketNummer: string; fotosFehlgeschlagen: number }> {
  const { data, error } = await supabase.rpc('portal_schaden_melden', {
    p_kategorie: kategorieLabel(m.kategorie),
    p_bereich: m.bereich,
    p_raum: raumGueltig(m.bereich, m.raum) ? m.raum : null,
    p_beschreibung: m.beschreibung.trim(),
    p_zutritt: m.zutritt,
    p_dringend: m.dringend,
  });
  if (error) throw new Error(error.message);
  const { id, ticket_nummer } = data as { id: string; ticket_nummer: string };

  let fehlgeschlagen = 0;
  for (const datei of m.fotos) {
    const endung = (datei.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 5) || 'jpg';
    const pfad = `${id}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${endung}`;
    const typ = fotoTyp(datei);
    const { error: upErr } = await supabase.storage.from('ticket-anhaenge').upload(pfad, datei, { upsert: false, contentType: typ });
    if (upErr) { fehlgeschlagen += 1; continue; }
    const { error: regErr } = await supabase.rpc('portal_foto_registrieren', {
      p_ticket: id, p_pfad: pfad, p_name: datei.name, p_typ: typ, p_groesse: datei.size,
    });
    if (regErr) fehlgeschlagen += 1;
  }
  return { ticketId: id, ticketNummer: ticket_nummer, fotosFehlgeschlagen: fehlgeschlagen };
}

// ── E5: Termine und Dokumente ──

export async function ladeMeineTermine(): Promise<PortalTermin[]> {
  const { data, error } = await supabase.rpc('portal_meine_termine');
  if (error) { console.error('Mieterportal: Termine laden fehlgeschlagen', error.message); return []; }
  return (data as PortalTermin[] | null) ?? [];
}

export async function ladeMeineDokumente(): Promise<PortalDokument[]> {
  const { data, error } = await supabase.rpc('portal_meine_dokumente');
  if (error) { console.error('Mieterportal: Dokumente laden fehlgeschlagen', error.message); return []; }
  return (data as PortalDokument[] | null) ?? [];
}

/** Lädt ein freigegebenes Dokument über die Edge Function portal-datei (prüft die Berechtigung bei jedem Abruf). */
export async function ladeDokumentHerunter(d: Pick<PortalDokument, 'id' | 'titel' | 'datei_name'>): Promise<void> {
  const { data: sitzung } = await supabase.auth.getSession();
  const token = sitzung.session?.access_token;
  if (!token) throw new Error('Bitte melden Sie sich erneut an.');
  const res = await fetch(`${supabaseUrl}/functions/v1/portal-datei?id=${encodeURIComponent(d.id)}`, {
    headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey },
  });
  if (!res.ok) {
    const meldung = await res.json().then((j: { error?: string }) => j.error).catch(() => null);
    throw new Error(meldung || 'Das Dokument konnte nicht geladen werden.');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = d.datei_name || d.titel || 'Dokument';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
